/**
 * Particles that gather around the hold button while it charges, and the stream that
 * rises from them to the spark.
 *
 * Kept free of DOM and clocks like spark.ts; the canvas feeds in charge and time.
 */

/** Look of the charge particles. Mutable for live tweaking; nothing else writes it. */
export const chargeTuning = {
  cloudCount: 320,
  /** Cloud radius, in button diameters: 0.5 gathers it over the button's rim. */
  cloudRadius: 0.5,
  /** How far out a particle starts before it has gathered, as a multiple of its radius. */
  gather: 1.6,
  /** Overall turn of the cloud, in radians per second. */
  spin: 0.15,
  streamCount: 26,
  /** Peak opacity of the stream, kept well below the cloud. */
  streamAlpha: 0.9,
  /** Stream passes per second, cloud to spark. */
  streamSpeed: 0.45,
  /** Sideways sway of the stream, in cloud radii. */
  streamSway: 0.1,
};

export interface CloudParticle {
  /** Offset from the button centre, in cloud radii. */
  x: number;
  y: number;
  /** Size multiplier, around 1. */
  size: number;
  alpha: number;
}

export interface StreamParticle {
  /** 0 at the top of the cloud, 1 at the spark. */
  t: number;
  /** Sideways offset from the straight path, in cloud radii. */
  offset: number;
  size: number;
  alpha: number;
}

const clamp01 = (x: number): number => Math.min(1, Math.max(0, x));
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

/** Fixed per-particle randomness in 0..1, so particles keep their place between frames. */
function hash(n: number): number {
  const x = Math.sin(n * 12.9898 + 4.1414) * 43758.5453;
  return x - Math.floor(x);
}

export function cloudParticles(
  charge: number,
  timeMs: number,
  reducedMotion = false,
  count = chargeTuning.cloudCount,
): CloudParticle[] {
  const c = clamp01(charge);
  const seconds = reducedMotion ? 0 : timeMs / 1000;
  const spin = seconds * chargeTuning.spin;
  const particles: CloudParticle[] = [];
  for (let i = 0; i < count; i++) {
    // Each particle joins at its own charge level, so the cloud fills in as it charges.
    const threshold = hash(i * 7 + 1) * 0.7;
    const visible = smoothstep(threshold, threshold + 0.2, c);
    if (visible <= 0) continue;

    // Ring-biased disk: dense towards the rim, sparse inside.
    const radius = 0.4 + 0.6 * Math.sqrt(hash(i * 13 + 2));
    const gathered = radius * lerp(chargeTuning.gather, 1, visible);
    const wobble = 0.03 * Math.sin(seconds * (0.8 + hash(i * 19 + 3)) + i);
    const angle = hash(i * 29 + 4) * 2 * Math.PI + spin;
    const twinkle = reducedMotion
      ? 1
      : 0.75 + 0.25 * Math.sin(seconds * (2 + 4 * hash(i * 31 + 5)) + i * 1.7);
    particles.push({
      x: Math.cos(angle) * (gathered + wobble),
      y: Math.sin(angle) * (gathered + wobble),
      size: (0.8 + hash(i * 37 + 6)) * (1 + 0.5 * c),
      alpha: visible * (0.6 + 0.4 * c) * twinkle,
    });
  }
  return particles;
}

export function streamParticles(
  charge: number,
  timeMs: number,
  reducedMotion = false,
  count = chargeTuning.streamCount,
): StreamParticle[] {
  // Starts once the cloud has begun to form.
  const strength = smoothstep(0.15, 0.6, clamp01(charge));
  if (strength <= 0) return [];
  const seconds = reducedMotion ? 0 : timeMs / 1000;
  const particles: StreamParticle[] = [];
  for (let i = 0; i < count; i++) {
    const jitter = hash(i * 41 + 7);
    const flow = seconds * chargeTuning.streamSpeed + (i + jitter * 0.5) / count;
    const t = flow - Math.floor(flow);
    // Fades in off the cloud, then thins out gradually on the way to the spark.
    const ends = smoothstep(0, 0.1, t) * (1 - smoothstep(0.3, 0.95, t));
    particles.push({
      t,
      offset: chargeTuning.streamSway * Math.sin(t * 9 + seconds * 2 + jitter * 6),
      // Cloud-sized where it leaves the cloud, thinning out towards the spark.
      size: (0.5 + jitter) * (0.8 + 0.4 * clamp01(charge)) * lerp(1, 0.5, smoothstep(0.3, 1, t)),
      alpha: chargeTuning.streamAlpha * strength * ends * (0.4 + 0.6 * clamp01(charge)),
    });
  }
  return particles;
}
